import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useState } from "react";
import { LeftSidebar } from "@/react/components/SettingsApp/LeftSidebar";

export interface LayoutConfig {
  disableRightSidebar?: boolean;
  disableSidebar?: boolean;
  hideAppBar?: boolean;
  renderLeftSidebar?: () => ReactNode;
  shouldHideSidebarNestedNavBar?: boolean;
}

export interface LayoutContextProps {
  config: LayoutConfig;
  mergeConfig: (updates: Partial<LayoutConfig>) => void;
  setConfig: Dispatch<SetStateAction<LayoutConfig>>;
}

const defaults: LayoutConfig = {
  disableRightSidebar: false,
  renderLeftSidebar: () => <LeftSidebar />,
  disableSidebar: false,
  hideAppBar: false,
  shouldHideSidebarNestedNavBar: false,
};

const LayoutContext = createContext<LayoutContextProps>({
  config: {},
  setConfig: () => {},
  mergeConfig: () => {},
});
LayoutContext.displayName = "LayoutContext";

export const LayoutContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [config, setConfig] = useState<LayoutConfig>(defaults);

  const resetToDefault = () => {
    setConfig(defaults);
  };
  const mergeConfig = (updates: Partial<LayoutConfig> = {}) => {
    setConfig({ ...config, ...updates });
  };

  const value = { config, setConfig, resetToDefault, mergeConfig };
  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
};

export const useLayoutContext = () => useContext(LayoutContext);
